import React, { useEffect, useState } from "react";
import "./styles.css";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { parseISO, format, startOfWeek, getDay } from "date-fns";
import ja from "date-fns/locale/ja";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : window.location.origin);

const MainPage = ({ userEmail }) => {
  const [name, setName] = useState("");

  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const [availableAccounts, setAvailableAccounts] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [selectedRoomKey, setSelectedRoomKey] = useState("");

  const [selectedAccount, setSelectedAccount] = useState("");
  const [message, setMessage] = useState("");
  const [authorizedMap, setAuthorizedMap] = useState({});
  const [allAccounts, setAllAccounts] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [meetings, setMeetings] = useState([]);

  const [invitees, setInvitees] = useState([{ name: "", email: "" }]);

  const [isCheckingAccounts, setIsCheckingAccounts] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState({});
  const [viewMode, setViewMode] = useState("list");
  const [filterAccount, setFilterAccount] = useState("");
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [currentView, setCurrentView] = useState("month");
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  const [deletingMeetingId, setDeletingMeetingId] = useState(null);
  const [initialMappingSeeded, setInitialMappingSeeded] = useState(false);
  const [googleProfileMap, setGoogleProfileMap] = useState({});
  const [creatorFilter, setCreatorFilter] = useState("all");

  // time parts
  const [startDate, setStartDate] = useState("");
  const [startHour, setStartHour] = useState("");
  const [startMinute, setStartMinute] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endHour, setEndHour] = useState("");
  const [endMinute, setEndMinute] = useState("");

  // Template selection + preview
  const [selectedTemplate, setSelectedTemplate] = useState("default"); // "default" | "kaneka_mid"
  const [isTemplateOpen, setIsTemplateOpen] = useState(false);

  const [availStatus, setAvailStatus] = useState("idle");

  const locales = { ja };
  const localizer = dateFnsLocalizer({
    format: (date, formatStr, options) =>
      format(date, formatStr, { ...options, locale: ja }),
    parse: (value) => parseISO(value),
    startOfWeek: () => startOfWeek(new Date(), { locale: ja }),
    getDay,
    locales: { ja },
  });

  // Session check
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await fetch(`${API_BASE}/current-user`, {
          credentials: "include",
        });
        if (res.status === 401) {
          const data = await res.json().catch(() => ({}));
          if (data?.error === "User not logged in") {
            alert("セッションの有効期限が切れました。ログインし直してください。");
            window.location.href = "/login";
          }
        }
      } catch (err) {
        console.error("セッションチェックに失敗しました:", err);
      }
    };
    checkSession();
  }, []);

  // Profile
  useEffect(() => {
    if (!userEmail) return;
    fetch(`${API_BASE}/user-profile`, { credentials: "include" })
      .then((res) => res.json())
      .then((profile) => {
        if (profile.email) {
          setGoogleProfileMap((prev) => ({
            ...prev,
            [profile.email]: {
              name: profile.name,
              picture: profile.picture,
            },
          }));
        }
      })
      .catch((err) => console.error("Failed to load profile:", err));
  }, [userEmail]);

  // Compose ISO
  const composeISO = (d, h, m) => (d && h && m ? `${d}T${h}:${m}` : "");

  useEffect(() => {
    setStartTime(composeISO(startDate, startHour, startMinute));
  }, [startDate, startHour, startMinute]);

  useEffect(() => {
    setEndTime(composeISO(endDate, endHour, endMinute));
  }, [endDate, endHour, endMinute]);

  // Seed mappings
  useEffect(() => {
    if (allAccounts.length > 0 && !initialMappingSeeded) {
      const seedMappingsFromEnv = async () => {
        try {
          const configuredIdsResponse = await fetch(
            `${API_BASE}/google/configured-calendar-ids`,
            { credentials: "include" }
          );
          if (!configuredIdsResponse.ok) return;
          const configuredCalendarIds = await configuredIdsResponse.json();

          const currentDbStatusResponse = await fetch(
            `${API_BASE}/google/calendar-mapping-status`,
            { credentials: "include" }
          );
          if (!currentDbStatusResponse.ok) return;
          const currentDbStatus = await currentDbStatusResponse.json();

          let mappingsWereUpdated = false;

          for (const acc of allAccounts) {
            const label = acc.label;
            const calendarIdFromEnv = configuredCalendarIds[label];
            const isCurrentlyLinkedInDb = currentDbStatus[label];

            if (label && calendarIdFromEnv && !isCurrentlyLinkedInDb) {
              const saveResponse = await fetch(
                `${API_BASE}/google/save-calendar-mapping`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  credentials: "include",
                  body: JSON.stringify({
                    zoom_label: label,
                    calendar_id: calendarIdFromEnv,
                  }),
                }
              );
              if (saveResponse.ok) {
                mappingsWereUpdated = true;
              } else {
                const errorResult = await saveResponse.json().catch(() => ({}));
                console.error(
                  `Failed to seed mapping for ${label}:`,
                  errorResult.error || saveResponse.statusText
                );
              }
            }
          }

          if (mappingsWereUpdated) {
            const updatedStatusResponse = await fetch(
              `${API_BASE}/google/calendar-mapping-status`,
              { credentials: "include" }
            );
            const updatedDbStatus = await updatedStatusResponse.json();
            setCalendarStatus(updatedDbStatus);
          }
          setInitialMappingSeeded(true);
        } catch (error) {
          console.error("Error during initial calendar mapping seeding:", error);
          setInitialMappingSeeded(true);
        }
      };

      seedMappingsFromEnv();
    }
  }, [allAccounts, initialMappingSeeded]);

  const loadMeetings = () => {
    fetch(`${API_BASE}/meetings`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => setMeetings(data))
      .catch((err) => console.error("Failed to load meetings:", err));
  };

  useEffect(() => {
    fetch(`${API_BASE}/authorized-accounts`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => setAuthorizedMap(data))
      .catch((err) => console.error("Failed to load authorized accounts:", err));

    fetch(`${API_BASE}/all-zoom-accounts`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => setAllAccounts(data.accounts || []))
      .catch((err) => console.error("Failed to load all Zoom accounts:", err));

    loadMeetings();
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/google/calendar-mapping-status`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => setCalendarStatus(data))
      .catch((err) =>
        console.error("Failed to load calendar mapping status:", err)
      );
  }, []);

  // Availability (both Zoom accounts and meeting rooms)
  const checkAvailability = async () => {
    if (
      !startDate ||
      !startHour ||
      !startMinute ||
      !endDate ||
      !endHour ||
      !endMinute
    ) {
      alert("開始時刻と終了時刻を入力してください。");
      return;
    }

    const startTimeStr = composeISO(startDate, startHour, startMinute);
    const endTimeStr = composeISO(endDate, endHour, endMinute);

    if (new Date(endTimeStr) <= new Date(startTimeStr)) {
      alert("終了時刻は開始時刻より後にしてください。");
      return;
    }

    setAvailStatus("checking");
    setIsCheckingAccounts(true);

    const payload = {
      start_time: startTimeStr,
      end_time: endTimeStr,
      ...(editingId ? { meeting_id: editingId } : {}),
      ...(selectedRoomKey ? { room_key: selectedRoomKey } : {}),
    };

    try {
      const res = await fetch(`${API_BASE}/available-accounts`, {
        credentials: "include",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      setAvailableAccounts(data.available_accounts || []);
      setAvailableRooms(data.available_rooms || []);
    } finally {
      setAvailStatus("ready");
      setIsCheckingAccounts(false);
    }
  };

  // Subject preview based on template + title
  const subjectPreview = (() => {
    if (selectedTemplate === "kaneka_mid") {
      return "カネカ特定保健指導　中間面談のご案内";
    }
    return "カネカ特定保健指導　ZoomURLのご案内";
  })();


  const handleSubmit = async (e) => {
    e.preventDefault();

    const startTimeStr = composeISO(startDate, startHour, startMinute);
    const endTimeStr = composeISO(endDate, endHour, endMinute);

    if (new Date(endTimeStr) <= new Date(startTimeStr)) {
      setMessage("❌ 終了時刻は開始時刻より後に設定してください。");
      return;
    }

    if (!selectedAccount) {
      setMessage("❌ Zoomアカウントを選択してください。");
      return;
    }

    setIsSubmitting(true);

    try {
      const filteredInvitees = invitees
        .map((i) => ({ name: (i.name || "").trim(), email: (i.email || "").trim() }))
        .filter((i) => i.email);

      const basePayload = {
        invitees: filteredInvitees,
        invite_emails: filteredInvitees.map((i) => i.email),
        account_label: selectedAccount,
        ...(selectedRoomKey ? { meeting_room_key: selectedRoomKey } : {}),
        email_template: selectedTemplate, // << NEW
      };

      const payload = editingId
        ? {
            ...basePayload,
            topic: name,
            start_time: startTimeStr,
            end_time: endTimeStr,
          }
        : {
            ...basePayload,
            name,
            start_time: startTimeStr,
            end_time: endTimeStr,
          };

      const url = editingId
        ? `${API_BASE}/meetings/${editingId}`
        : `${API_BASE}/schedule`;

      const res = await fetch(url, {
        credentials: "include",
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (res.ok) {
        setMessage(`✅ ミーティング${editingId ? "を更新しました" : "を作成しました"}`);
        setName("");
        setStartDate("");
        setStartHour("");
        setStartMinute("");
        setEndDate("");
        setEndHour("");
        setEndMinute("");
        setInvitees([{ name: "", email: "" }]);
        setSelectedAccount("");
        setSelectedRoomKey("");
        setAvailableRooms([]);
        setAvailableAccounts([]);
        setAvailStatus("idle");
        setSelectedTemplate("default"); // reset
        setEditingId(null);
        loadMeetings();
      } else {
        setMessage(`❌ エラー: ${data.error || "送信に失敗しました。"}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Invite rows
  const handleInviteChange = (index, field, value) => {
    const updated = [...invitees];
    updated[index] = { ...updated[index], [field]: value };
    setInvitees(updated);
  };

  const handleAddInvite = () => {
    setInvitees([...invitees, { name: "", email: "" }]);
  };

  const handleRemoveInvite = (index) => {
    const updated = invitees.filter((_, i) => i !== index);
    setInvitees(updated.length === 0 ? [{ name: "", email: "" }] : updated);
  };

  const handleEdit = (meeting) => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setName(meeting.topic);

const toLocalParts = (isoString) => {
  if (!isoString) return { date: "", hour: "", minute: "" };

  // Parse whatever backend sent (it is UTC)
  let d = new Date(isoString);

  // Always convert UTC → JST (+9 hours)
  const jst = new Date(d.getTime() + 9 * 60 * 60 * 1000);

  const year = jst.getFullYear();
  const month = String(jst.getMonth() + 1).padStart(2, "0");
  const day = String(jst.getDate()).padStart(2, "0");
  const hour = String(jst.getHours()).padStart(2, "0");
  const minute = String(jst.getMinutes()).padStart(2, "0");

  return { date: `${year}-${month}-${day}`, hour, minute };
};

    const start = toLocalParts(meeting.start_time);
    const end = toLocalParts(meeting.end_time);

    setStartDate(start.date);
    setStartHour(start.hour);
    setStartMinute(start.minute);
    setEndDate(end.date);
    setEndHour(end.hour);
    setEndMinute(end.minute);

    if (Array.isArray(meeting.invitees) && meeting.invitees.length) {
      setInvitees(meeting.invitees.map((i) => ({ name: i.name || "", email: i.email || "" })));
    } else {
      setInvitees(
        (meeting.invite_emails && meeting.invite_emails.length ? meeting.invite_emails : [""]).map(
          (e) => ({ name: "", email: e })
        )
      );
    }

    setSelectedAccount(meeting.zoom_label);
    setSelectedRoomKey(meeting.meeting_room_key || "");

    // If backend starts returning the saved template (optional), prefill it.
    setSelectedTemplate(meeting.email_template || "default");

    setEditingId(meeting.id);
    setMessage("✏️ 編集モード中");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("このミーティングを削除してもよろしいですか？")) return;

    setDeletingMeetingId(id);

    try {
      const res = await fetch(`${API_BASE}/meetings/${id}`, {
        credentials: "include",
        method: "DELETE",
      });

      if (res.ok) {
        setMessage("🗑️ ミーティングを削除しました。");
        loadMeetings();
      } else {
        const data = await res.json().catch(() => ({ error: "削除に失敗しました。" }));
        setMessage(`❌ 削除に失敗しました: ${data.error || res.statusText}`);
      }
    } catch (err) {
      console.error("Failed to delete meeting:", err);
      setMessage("削除リクエストの送信に失敗しました。");
    } finally {
      setDeletingMeetingId(null);
    }
  };

  const handleGoogleAuthorize = (zoomLabel) => {
    window.location.href = `${API_BASE}/google/authorize/${zoomLabel}`;
  };

  const filteredMeetings = meetings.filter((m) => {
    const accountMatch = !filterAccount || m.zoom_label === filterAccount;
    const creatorMatch = creatorFilter === "all" || m.creator_email === userEmail;
    return accountMatch && creatorMatch;
  });


  const events = filteredMeetings.map((m) => ({
    id: m.id,
    title: m.topic,
    start: m.start_time ? new Date(m.start_time) : new Date(),
    end: m.end_time ? new Date(m.end_time) : new Date(),
    url: m.zoom_url,
    creator_email: m.creator_email,
    meeting_room_key: m.meeting_room_key,
    resource: { zoomLabel: m.zoom_label },
  }));



  const fallbackColors = [
    "#42a5f5",
    "#66bb6a",
    "#ff7043",
    "#ab47bc",
    "#ffa726",
    "#26c6da",
    "#8d6e63",
    "#ec407a",
    "#7e57c2",
    "#9ccc65",
  ];

  const accountColors = allAccounts.reduce((map, acc, index) => {
    map[acc.label] = fallbackColors[index % fallbackColors.length];
    return map;
  }, {});

  const CustomToolbar = ({ label, onNavigate, onView, views }) => (
    <div className="rbc-toolbar">
      <span className="rbc-btn-group">
        <button type="button" onClick={() => onNavigate("PREV")}>＜ 前へ</button>
        <button type="button" onClick={() => onNavigate("TODAY")}>今日</button>
        <button type="button" onClick={() => onNavigate("NEXT")}>次へ ＞</button>
      </span>
      <span className="rbc-toolbar-label">{label}</span>
      <span className="rbc-btn-group">
        {views.includes("month") && <button type="button" onClick={() => onView("month")}>月</button>}
        {views.includes("week") && <button type="button" onClick={() => onView("week")}>週</button>}
        {views.includes("day") && <button type="button" onClick={() => onView("day")}>日</button>}
      </span>
    </div>
  );




const formatMeetingTime = (start, end) => {
  console.log("🟦 Raw start:", start);
  console.log("🟦 Raw end:", end);

  try {
    const toJST = (value) => {
      const d = new Date(value);
      console.log("➡️ Parsed Date:", d.toString());
      // force +9h always
      const jst = new Date(d.getTime() + 9 * 3600 * 1000);
      console.log("➡️ JST Date:", jst.toString());
      return jst;
    };

    const startDate = toJST(start);
    const endDate = toJST(end);

    const days = ["日", "月", "火", "水", "木", "金", "土"];
    const weekday = days[startDate.getDay()];

    const sH = String(startDate.getHours()).padStart(2, "0");
    const sM = String(startDate.getMinutes()).padStart(2, "0");
    const eH = String(endDate.getHours()).padStart(2, "0");
    const eM = String(endDate.getMinutes()).padStart(2, "0");

    const result = `${startDate.getFullYear()}年${startDate.getMonth() + 1}月${startDate.getDate()}日（${weekday}） ${sH}:${sM}〜${eH}:${eM}`;

    console.log("🟩 Final Output:", result);

    return result;

  } catch (err) {
    console.error("❌ formatMeetingTime error:", err);
    return "日時エラー";
  }
};







  const formatTemplateTimeJST = () => {
    if (!startDate || !startHour || !startMinute || !endDate || !endHour || !endMinute) {
      return "月　日（）　:　～　";
    }
    const start = new Date(`${startDate}T${startHour}:${startMinute}:00+09:00`);
    const end = new Date(`${endDate}T${endHour}:${endMinute}:00+09:00`);
    const days = ["日","月","火","水","木","金","土"];
    const mm = String(start.getMonth() + 1);
    const dd = String(start.getDate());
    const dow = days[start.getDay()];
    const sHH = String(start.getHours()).padStart(2, "0");
    const sMM = String(start.getMinutes()).padStart(2, "0");
    const eHH = String(end.getHours()).padStart(2, "0");
    const eMM = String(end.getMinutes()).padStart(2, "0");
    return `${mm}月　${dd}日（${dow}）　${sHH}:${sMM}　～　${eHH}:${eMM}`;
  };

  return (
    <div className="container">
      <div className="sidebar">
        <div className="sidebar-section">
          {userEmail && (
            <div className="user-profile">
              <img
                src={
                  googleProfileMap[userEmail]?.picture ||
                  "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                }
                alt="User"
                className="user-avatar"
              />
              <div className="user-info">
                <strong>
                  {googleProfileMap[userEmail]?.name || userEmail.split("@")[0]}
                </strong>
                <p>{userEmail}</p>
                <button
                  type="button"
                  className="logout-btn"
                  onClick={() => {
                    fetch(`${API_BASE}/logout`, {
                      method: "GET",
                      credentials: "include",
                    })
                      .then((res) => {
                        if (res.ok) {
                          window.location.href = "/login";
                        } else {
                          alert("ログアウトに失敗しました");
                        }
                      })
                      .catch((err) => {
                        console.error("Logout failed:", err);
                        alert("ログアウトに失敗しました");
                      });
                  }}
                >
                  ログアウト
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="sidebar-section">
          <h3>アカウント状況</h3>
          {allAccounts.map((acc) => (
            <div key={acc.user_id} className="account-box">
              <strong>{acc.label}</strong>
              <p>
                {calendarStatus[acc.label]
                  ? "✅ Googleカレンダー連携済み"
                  : "❌ 未連携"}
              </p>
            </div>
          ))}
        </div>

        <div className="sidebar-section functions-list">
          <h3>機能一覧</h3>
          <ul>
            <li>複数のZoomアカウントでミーティングを作成可能</li>
            <li>ミーティング・会議室の重複を防止</li>
            <li>テンプレメールを自動作成・送信</li>
            <li>ミーティングの1週間前と1日前にリマインドメールを自動送信</li>
            <li>Googleカレンダー自動追加</li>
          </ul>
        </div>

        <div className="sidebar-section document-link-section">
          <h3>マニュアル</h3>
          <div style={{ textAlign: "center" }}>
            <button
              type="button"
              className="manual-btn"
              onClick={() =>
                window.open(
                  "https://docs.google.com/document/d/19plYQETFA7q0M6I1PMKIveghFcE45RHzHcPJDPcfkYc/edit?usp=sharing",
                  "_blank"
                )
              }
            >
              📄 仕様書を開く
            </button>
          </div>
        </div>
      </div>

      <div className="main-content">
        <h2 className="section-heading">📅 Zoom予約システム - DH向け</h2>
        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label htmlFor="meetingTitle" className="label">📝 ミーティングタイトル</label>
            <input
              id="meetingTitle"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="ミーティングタイトル"
              required
            />
          </div>

          {/* 開始 */}
          <div className="form-group">
            <label className="label">🕐 開始時刻</label>
            <div className="datetime-container">
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setAvailStatus("idle");
                  setAvailableAccounts([]);
                  setAvailableRooms([]);
                  setSelectedAccount("");
                  setSelectedRoomKey("");
                }}
                required
              />
              <div className="time-selectors">
                <select
                  value={startHour}
                  onChange={(e) => {
                    setStartHour(e.target.value);
                    setAvailStatus("idle");
                    setAvailableAccounts([]);
                    setAvailableRooms([]);
                    setSelectedAccount("");
                    setSelectedRoomKey("");
                  }}
                  required
                >
                  <option value="">時</option>
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={String(i).padStart(2, "0")}>
                      {String(i).padStart(2, "0")}
                    </option>
                  ))}
                </select>
                <span>:</span>
                <select
                  value={startMinute}
                  onChange={(e) => {
                    setStartMinute(e.target.value);
                    setAvailStatus("idle");
                    setAvailableAccounts([]);
                    setAvailableRooms([]);
                    setSelectedAccount("");
                    setSelectedRoomKey("");
                  }}
                  required
                >
                  <option value="">分</option>
                  {["00", "10", "20", "30", "40", "50"].map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 終了 */}
          <div className="form-group">
            <label className="label">🕔 終了時刻</label>
            <div className="datetime-container">
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setAvailStatus("idle");
                  setAvailableAccounts([]);
                  setAvailableRooms([]);
                  setSelectedAccount("");
                  setSelectedRoomKey("");
                }}
                required
              />
              <div className="time-selectors">
                <select
                  value={endHour}
                  onChange={(e) => {
                    setEndHour(e.target.value);
                    setAvailStatus("idle");
                    setAvailableAccounts([]);
                    setAvailableRooms([]);
                    setSelectedAccount("");
                    setSelectedRoomKey("");
                  }}
                  required
                >
                  <option value="">時</option>
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={String(i).padStart(2, "0")}>
                      {String(i).padStart(2, "0")}
                    </option>
                  ))}
                </select>
                <span>:</span>
                <select
                  value={endMinute}
                  onChange={(e) => {
                    setEndMinute(e.target.value);
                    setAvailStatus("idle");
                    setAvailableAccounts([]);
                    setAvailableRooms([]);
                    setSelectedAccount("");
                    setSelectedRoomKey("");
                  }}
                  required
                >
                  <option value="">分</option>
                  {["00", "10", "20", "30", "40", "50"].map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Invitees (Name + Email) */}
          <div className="email-input-group">
            <label>📧 招待者（氏名＋メール）</label>
            {invitees.map((row, index) => (
              <div
                key={index}
                className="email-field-row"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1.5fr auto",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <input
                  type="text"
                  value={row.name}
                  onChange={(e) => handleInviteChange(index, "name", e.target.value)}
                  placeholder="氏名（任意）"
                />
                <input
                  type="email"
                  value={row.email}
                  onChange={(e) => handleInviteChange(index, "email", e.target.value)}
                  placeholder="メールアドレス"
                />
                {invitees.length > 1 && (
                  <button
                    type="button"
                    className="remove-email-btn"
                    onClick={() => handleRemoveInvite(index)}
                    aria-label="Remove invitee"
                  >
                    ✖
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="add-email-btn" onClick={handleAddInvite}>
              ➕ 氏名・メールを追加
            </button>
          </div>

          {/* Template selector + subject preview */}
          <div className="form-group" style={{ marginTop: 8 }}>
            <label className="label">📄 メールテンプレート</label>
            <select
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="full-width-input"
            >
              <option value="default">① カネカ特定保健指導　ZoomURLのご案内</option>
              <option value="kaneka_mid">② カネカ 中間面談のご案内</option>
            </select>
          </div>

          <div className="form-group" style={{ marginTop: 6 }}>
            <label className="label">✉ 件名プレビュー</label>
            <input 
              type="text"
              value={subjectPreview}
              readOnly
              className="full-width-input"
            />
          </div>


          {/* Template (collapsible) */}
          <div className="template-section">
            <button
              type="button"
              className="template-toggle"
              onClick={() => setIsTemplateOpen((v) => !v)}
              aria-expanded={isTemplateOpen}
              aria-controls="template-panel"
            >
              {isTemplateOpen ? "▼ メール文面を閉じる" : "▶ メール文面を表示"}
            </button>

            {isTemplateOpen && (
              <div id="template-panel" className="message-template-preview">
                {selectedTemplate === "kaneka_mid" ? (
                  <>
                    <p>{(invitees[0]?.name?.trim() || "【対象者】")}様</p>
                    <p>
                      特定保健指導にご参加いただきありがとうございます。<br />
                      株式会社カネカ及びカネカ健康保険組合様より特定保健指導業務の委託を受けております<br />
                      ジェイエムシー株式会社の山﨑と申します。
                    </p>
                    <p>
                      ZOOMのURLを送付いたします。<br />
                      時間になりましたら、以下のURLよりご参加よろしくお願いいたします。
                    </p>
                    <p>
                      日時：{formatTemplateTimeJST()}<br />
                      ZOOM URL：<span style={{ opacity: 0.7 }}>(作成後に自動挿入)</span><br />
                      ミーティングID：<span style={{ opacity: 0.7 }}>(作成後に自動挿入)</span><br />
                      パスワード：<span style={{ opacity: 0.7 }}>(作成後に自動挿入)</span>
                    </p>
                    <p>ご確認のほど、よろしくお願いいたします。</p>
                    <p>
                      -----------------------------------------------<br />
                      特定保健指導実施機関　ジェイエムシー株式会社<br />
                      担当：山﨑<br />
                      <br />
                      お問合せ先<br />
                      mail：tokuho@jmc-ltd.co.jp<br />
                      TEL:　0120-087-006（平日10時～17時）<br />
                      -----------------------------------------------<br />
                    </p>
                  </>
                ) : (
                  <>
                    <p>{(invitees[0]?.name?.trim() || "【対象者】")}様</p>
                    <p>
                      特定保健指導にお申込みいただきありがとうございます。<br />
                      株式会社カネカ及びカネカ健康保険組合様より特定保健指導業務の委託を受けております<br />
                      ジェイエムシー株式会社の山﨑と申します。
                    </p>
                    <p>
                      ZOOMのURLを送付いたします。<br />
                      時間になりましたら、以下のURLよりご参加よろしくお願いいたします。
                    </p>
                    <p>
                      日時：{formatTemplateTimeJST()}<br />
                      ZOOM URL：<span style={{ opacity: 0.7 }}>(作成後に自動挿入)</span><br />
                      ミーティングID：<span style={{ opacity: 0.7 }}>(作成後に自動挿入)</span><br />
                      パスワード：<span style={{ opacity: 0.7 }}>(作成後に自動挿入)</span>
                    </p>
                    <p>
                      また、別メールで保健指導システムへのログイン依頼メールを送付いたします。<br />
                      本メール添付のマニュアルをご確認いただき、登録をお願いいたします。<br />
                      （※登録は必須ではございません。）<br />
                      ご不明点等ございましたら初回面談時に説明させていただきます。
                    </p>
                    <p>ご確認のほど、よろしくお願いいたします。</p>
                    <p>
                      -----------------------------------------------<br />
                      特定保健指導実施機関　ジェイエムシー株式会社<br />
                      担当：山﨑<br />
                      <br />
                      お問合せ先<br />
                      mail：tokuho@jmc-ltd.co.jp<br />
                      TEL:　0120-087-006（平日10時～17時）<br />
                      -----------------------------------------------<br />
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Availability button (checks Zoom accounts + meeting rooms) */}
          <button
            type="button"
            onClick={checkAvailability}
            disabled={
              isCheckingAccounts ||
              !startDate ||
              !startHour ||
              !startMinute ||
              !endDate ||
              !endHour ||
              !endMinute
            }
          >
            {isCheckingAccounts
              ? "確認中..."
              : "利用可能なZoomアカウントと会議室を確認"}
            {isCheckingAccounts && <span className="inline-spinner" />}
          </button>

          {/* Availability results panel */}
          {(availableAccounts.length > 0 || availableRooms.length > 0) && (
            <div
              className="availability-panel"
              style={{
                marginTop: 12,
                padding: 12,
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                background: "#fafafa",
              }}
            >
              {/* Zoom account select */}
              {availableAccounts.length > 0 ? (
                <>
                  <label
                    htmlFor="zoomAccountSelect"
                    className="label"
                    style={{ marginTop: "6px", display: "block" }}
                  >
                    利用可能なZoomアカウント:
                  </label>
                  <select
                    id="zoomAccountSelect"
                    value={selectedAccount}
                    onChange={(e) => setSelectedAccount(e.target.value)}
                    required
                  >
                    <option value="">Zoomアカウントを選択</option>
                    {availableAccounts.map((acc) => (
                      <option key={acc.user_id} value={acc.label}>
                        {acc.label}
                      </option>
                    ))}
                  </select>
                </>
              ) : (
                <p style={{ margin: "4px 0" }}>
                  この時間帯で利用可能なZoomアカウントはありません。
                </p>
              )}

              {/* Meeting room select (shown after the account select and only after availability check) */}
              <div className="form-group" style={{ marginTop: 12 }}>
                <label className="label">🏢 会議室（任意）</label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <select
                    value={selectedRoomKey}
                    onChange={(e) => setSelectedRoomKey(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="">（会議室を選択しない）</option>
                    {availableRooms.map((r) => (
                      <option key={r.calendar_id} value={r.label}>
                        {r.label}
                      </option>
                    ))}
                  </select>

                </div>
                <small style={{ display: "block", opacity: 0.75, marginTop: 4 }}>
                  ※ 会議室の空き状況はこのパネル内の選択内容に合わせて判定されます（部屋変更で自動再チェック）
                </small>
              </div>

              {/* Submit only appears when at least one Zoom account is available */}
              {availableAccounts.length > 0 && selectedAccount && (
                <button type="submit" disabled={isSubmitting} style={{ marginTop: 12 }}>
                  {isSubmitting
                    ? editingId
                      ? "更新中..."
                      : "作成中..."
                    : editingId
                    ? "ミーティングを更新"
                    : "ミーティングを作成"}
                  {isSubmitting && <span className="inline-spinner" />}
                </button>
              )}
            </div>
          )}

          {/* Message when checked but nothing available */}
          {/* Before check */}
          {availStatus === "idle" && (
            <p style={{ marginTop: 10, opacity: 0.6 }}>
              「利用可能なZoomアカウントと会議室を確認」ボタンを押してください。
            </p>
          )}

          {/* While checking */}
          {availStatus === "checking" && (
            <p style={{ marginTop: 10 }}>空き状況を確認中...</p>
          )}

          {/* After check, show warning only if truly none */}
          {availStatus === "ready" &&
            availableAccounts.length === 0 &&
            availableRooms.length === 0 && (
              <p style={{ marginTop: 10 }}>
                選択された時間帯で利用可能なZoomアカウント／会議室はありません。
              </p>
            )}

        </form>

        {message && <p className="message-display">{message}</p>}

        <hr />

        <div className="view-switch">
          <button
            type="button"
            className={viewMode === "list" ? "active-view" : ""}
            onClick={() => setViewMode("list")}
          >
            📋 リストビュー
          </button>
          <button
            type="button"
            className={viewMode === "calendar" ? "active-view" : ""}
            onClick={() => setViewMode("calendar")}
          >
            📅 カレンダービュー
          </button>

          <div className="calendar-controls">
            <div className="filter-row">
              <label htmlFor="creatorFilter" style={{ marginRight: 5 }} />
              <select
                id="creatorFilter"
                value={creatorFilter}
                onChange={(e) => setCreatorFilter(e.target.value)}
                className="creator-filter"
              >
                <option value="all">すべての予定</option>
                <option value="mine">自分の予定</option>
              </select>
              <label htmlFor="accountFilter" style={{ margin: "0 5px 0 10px" }} />
              <select
                id="accountFilter"
                value={filterAccount}
                onChange={(e) => setFilterAccount(e.target.value)}
                className="account-filter"
              >
                <option value="">すべてのZoomアカウント</option>
                {allAccounts.map((acc) => (
                  <option key={acc.user_id} value={acc.label}>
                    {acc.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="calendar-legend">
              {allAccounts.map((acc, index) => (
                <span key={index}>
                  <span
                    className="legend-dot"
                    style={{ background: accountColors[acc.label] || "#ccc" }}
                  />
                  {acc.label}
                </span>
              ))}
            </div>
          </div>
        </div>

        <h3>📅 登録済みのミーティング</h3>

        {meetings.length === 0 ? (
          <p>ミーティングはまだありません。</p>
        ) : viewMode === "list" ? (
          <div className="meeting-list-scroll-container">
            <ul className="meeting-list">
              {filteredMeetings
                .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                .map((meeting) => {
                  const creator = googleProfileMap[meeting.creator_email] || {};
                  return (
                    <li
                      key={meeting.id}
                      className="meeting-item"
                      style={{
                        backgroundColor:
                          (accountColors[meeting.zoom_label] || "#ccc") + "20",
                      }}
                    >
                      <strong>{meeting.topic}</strong>
                      <br />
                      {formatMeetingTime(meeting.start_time, meeting.end_time)}
                      <br />
                      <strong>アカウント:</strong> {meeting.zoom_label}
                      <br />
                      {meeting.meeting_room_key && (
                        <>
                          <strong>会議室:</strong> {meeting.meeting_room_key}
                          <br />
                        </>
                      )}
                      <strong>作成者:</strong> {creator.name || meeting.creator_email}
                      <br />
                      <a
                        href={meeting.zoom_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {meeting.zoom_url}
                      </a>
                      <br />

                      {meeting.creator_email === userEmail && (
                        <>
                          <button
                            type="button"
                            className="edit-btn"
                            onClick={() => handleEdit(meeting)}
                          >
                            ✏️ 編集
                          </button>
                          <button
                            type="button"
                            className="delete-btn"
                            onClick={() => handleDelete(meeting.id)}
                            disabled={deletingMeetingId === meeting.id}
                          >
                            {deletingMeetingId === meeting.id ? (
                              <span className="inline-spinner" />
                            ) : (
                              "🗑️ 削除"
                            )}
                          </button>
                        </>
                      )}
                    </li>
                  );
                })}
            </ul>
          </div>
        ) : (
          <div className="big-calendar-container">
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              view={currentView}
              onView={(view) => setCurrentView(view)}
              date={calendarDate}
              onNavigate={(date) => setCalendarDate(date)}
              defaultView="month"
              views={["month", "week", "day"]}
              style={{ height: 600 }}
              popup
              onSelectEvent={(event) => {
                const fullMeeting = meetings.find((m) => m.id === event.id);
                setSelectedMeeting({
                  ...event,
                  ...(fullMeeting && {
                    topic: fullMeeting.topic,
                    start_time: fullMeeting.start_time,
                    end_time: fullMeeting.end_time,
                    invite_emails: fullMeeting.invite_emails,
                    meeting_room_key: fullMeeting.meeting_room_key,
                    email_template: fullMeeting.email_template, // if backend returns it later
                  }),
                  zoom_label: event.resource?.zoomLabel || "（不明）",
                });
              }}
              messages={{
                today: "今日",
                previous: "前へ",
                next: "次へ",
                month: "月",
                week: "週",
                day: "日",
                agenda: "予定",
                showMore: (total) => `＋${total} 件`,
              }}
              components={{ toolbar: CustomToolbar }}
              eventPropGetter={(event) => {
                const zoomLabel = event.resource?.zoomLabel;
                const backgroundColor = accountColors[zoomLabel] || "#90a4ae";
                return {
                  style: {
                    backgroundColor,
                    color: "white",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "2px 6px",
                    fontWeight: "bold",
                  },
                };
              }}
            />

            {selectedMeeting && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <h2 className="modal-title">{selectedMeeting.title}</h2>
                  <ul className="meeting-list">
                    <li
                      className="meeting-item"
                      style={{
                        backgroundColor:
                          (accountColors[selectedMeeting.zoom_label] || "#ccc") +
                          "20",
                      }}
                    >
                      {formatMeetingTime(
                        selectedMeeting.start,
                        selectedMeeting.end
                      )}
                      <br />
                      <strong>アカウント:</strong> {selectedMeeting.zoom_label}
                      <br />
                      {selectedMeeting.meeting_room_key && (
                        <>
                          <strong>会議室:</strong> {selectedMeeting.meeting_room_key}
                          <br />
                        </>
                      )}
                      <strong>作成者:</strong> {selectedMeeting.creator_email}
                      <br />
                      <a
                        href={selectedMeeting.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {selectedMeeting.url}
                      </a>
                      <br />

                      {selectedMeeting.creator_email === userEmail && (
                        <>
                          <button
                            type="button"
                            className="edit-btn"
                            onClick={() => {
                              const fullMeetingToEdit =
                                meetings.find((m) => m.id === selectedMeeting.id) ||
                                selectedMeeting;
                              handleEdit(fullMeetingToEdit);
                              setSelectedMeeting(null);
                            }}
                          >
                            ✏️ 編集
                          </button>
                          <button
                            type="button"
                            className="delete-btn"
                            onClick={() => {
                              handleDelete(selectedMeeting.id);
                              setSelectedMeeting(null);
                            }}
                            disabled={deletingMeetingId === selectedMeeting.id}
                          >
                            {deletingMeetingId === selectedMeeting.id ? (
                              <span className="inline-spinner" />
                            ) : (
                              "🗑️ 削除"
                            )}
                          </button>
                        </>
                      )}
                    </li>
                  </ul>
                  <div style={{ textAlign: "right" }}>
                    <button
                      type="button"
                      className="close-modal-btn"
                      onClick={() => setSelectedMeeting(null)}
                    >
                      ❌ 閉じる
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="page-watermark">
          JMC株式会社
          <br />
          DX事業推進室
        </div>
      </div>
    </div>
  );
};

export default MainPage;
